import type { ReactNode } from 'react';

import { CloseIcon } from '@gearbelt/ui/icons';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { useSiteBannerStore } from '@/stores/site-banner.store';

const siteBannerVariants = cva('flex w-full items-center gap-3 border-b px-4 py-3 text-sm', {
  variants: {
    variant: {
      info: 'border-default-blue/30 bg-default-light-blue text-default-dark-blue',
      warning: 'border-default-yellow bg-default-light-yellow text-default-text',
    },
  },
  defaultVariants: {
    variant: 'info',
  },
});

interface SiteBannerProps extends VariantProps<typeof siteBannerVariants> {
  id: string;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
  dismissible?: boolean;
}

export function SiteBanner({ id, variant, icon, children, className, dismissible = true }: SiteBannerProps) {
  const isDismissed = useSiteBannerStore((state) => state.dismissedBannerIds.includes(id));
  const dismissBanner = useSiteBannerStore((state) => state.dismissBanner);

  if (dismissible && isDismissed) return null;

  return (
    <div role="status" className={cn(siteBannerVariants({ variant }), className)}>
      {icon && <span className="flex shrink-0 items-center">{icon}</span>}
      <div className="flex-1">{children}</div>
      {dismissible && (
        <button
          type="button"
          onClick={() => dismissBanner(id)}
          aria-label="Dismiss notification"
          className="-mr-1 flex shrink-0 items-center rounded-full p-1 transition-colors hover:bg-black/10">
          <CloseIcon size={20} />
        </button>
      )}
    </div>
  );
}
